// One RFI, opened from the list: the header fields, the question, the sheets,
// the pictures and the locations, then Build / Send for review / Approve.
//
// THE RECORD IS THE INPUT, THE PDF IS BUILT FROM IT (back/models/rfi). The
// page edits a local copy and saves it with the revision it was opened at;
// a save from a stale revision is refused by the server and said here. A
// build always uses the SAVED record, so Build saves first.
//
// What each button does is the server's answer (`rfi.can`), so the page never
// offers an action the server will refuse.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Col, DatePicker, Divider, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Row,
  Select, Space, Spin, Switch, Tag, Tooltip, Typography, Upload, message,
} from "antd";
import {
  CheckOutlined, DeleteOutlined, EditOutlined, FilePdfOutlined, PlusOutlined, RollbackOutlined, SaveOutlined, SendOutlined,
  ToolOutlined, UploadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  approveRfi, buildRfi, deleteRfi, deleteRfiImage, fetchRfiImage, fetchRfiPdf, listRfiSheets, markRfiSubmitted,
  reopenRfi, saveRfi, sendRfiForReview, uploadRfiImages,
} from "api/rfi/rfiApi";
import LocationPicker from "components/Coordination/LocationPicker";
import { ExtraScopeChip } from "components/Coordination/ExtraScope";
import { STATUS } from "./rfiStatus";
import SheetMarkup from "./SheetMarkup";

const { Text } = Typography;

// The question is typed as one text, paragraphs separated by a blank line.
const toParagraphs = (text) => String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
const fromParagraphs = (list) => (list || []).join("\n\n");

function copyOf(rfi) {
  return {
    rev: rfi.rev,
    header: { ...rfi.header, drawingNumbers: [...((rfi.header && rfi.header.drawingNumbers) || [])] },
    question: fromParagraphs(rfi.draft && rfi.draft.questionParagraphs),
    asks: [...((rfi.draft && rfi.draft.numberedAsks) || [])],
    sheets: ((rfi.sources && rfi.sources.sheets) || []).map((s) => ({ ...s })),
    images: ((rfi.sources && rfi.sources.images) || []).map((i) => ({ ...i })),
    locations: (rfi.locations || []).map((l) => ({ ...l, imageIds: [...(l.imageIds || [])], clouds: (l.clouds || []).map((r) => [...r]) })),
    // The picked place (back/models/rfi scope). Only the three names travel:
    // areaId is the server's own resolution of the area and is never sent
    // back, so the page cannot pin a drawing to a zone that was renamed or
    // reissued under it.
    scope: {
      level: (rfi.scope && rfi.scope.level) || "",
      area: (rfi.scope && rfi.scope.area) || "",
      system: (rfi.scope && rfi.scope.system) || "",
    },
  };
}
const bodyOf = (c) => ({
  rev: c.rev,
  header: c.header,
  draft: { questionParagraphs: toParagraphs(c.question), numberedAsks: c.asks.map((a) => a.trim()).filter(Boolean) },
  sheets: c.sheets,
  images: c.images.map((i) => ({ id: i.id, caption: i.caption || "" })),
  locations: c.locations,
  scope: c.scope,
});

// A picture behind the token: fetched as a blob, shown from an object URL.
function AuthImage({ projectId, rfiId, imageId, style }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let live = true;
    let made = "";
    fetchRfiImage(projectId, rfiId, imageId)
      .then((b) => { if (live) { made = URL.createObjectURL(b); setSrc(made); } })
      .catch(() => {});
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [projectId, rfiId, imageId]);
  return src ? <img alt="" src={src} style={style} /> : <div style={{ ...style, background: "#f5f5f5" }} />;
}

// WHERE THIS RFI IS, picked from the project's own lists.
//
// The cover has always printed header.location, a free line somebody types
// ("Level 3 - Grid C-D / 4-5"). That line is right for a reader and tells us
// nothing: an RFI whose location said "North bay" could not be placed in the
// building at all, so the GC/Owner sign-off could not tell whether the RFI
// was asking about scope the client had already accepted.
//
// These three are the SAME pickers an issue is raised with
// (components/Coordination/LocationPicker), so an RFI and an issue in one
// place carry one set of strings. They are what rfi.extraScope is decided
// from (back/utils/rfiScope).
//
// THE TYPED LINE IS NOT OVERWRITTEN. Picking a place fills the Location line
// while it is still empty, as an offer; once there are words in it they are
// the writer's and stay.
const PICKED = /^(area|arch):/;
const scopeLine = (sc) => [sc.level, PICKED.test(sc.area || "") ? "" : sc.area, sc.system]
  .map((v) => String(v || "").trim()).filter(Boolean).join(" - ");

function ScopeFields({ projectId, c, set, disabled }) {
  const sc = c.scope || {};
  const pick = (v) => {
    const next = { level: v.level || "", area: v.area || "", system: v.system || "" };
    const typed = String((c.header && c.header.location) || "").trim();
    const header = typed ? c.header : { ...c.header, location: scopeLine(next) };
    set({ scope: next, header });
  };
  return (
    <Form layout="vertical">
      <Form.Item
        label="Where this RFI is"
        style={{ marginBottom: 10 }}
        extra="The level, area and system this question is about - the same lists issues are raised against. It places the RFI on the project, and it is how an RFI about scope the owner/GC has signed off is marked as extra scope."
      >
        <LocationPicker
          projectId={projectId}
          value={{ level: sc.level || "", area: sc.area || "", system: sc.system || "" }}
          onChange={pick}
          fields={["level", "area", "system"]}
          disabled={disabled}
          fill
        />
      </Form.Item>
    </Form>
  );
}

const FIELD_HELP = "Write {exhibitPages:sheet}, {exhibitPages:views} or {exhibitPages:locationRows} to cite pages - the numbers are filled in when the package is built.";

function HeaderFields({ c, set, disabled }) {
  const h = c.header;
  const field = (key, label, props = {}) => (
    <Form.Item label={label} style={{ marginBottom: 10 }}>
      <Input value={h[key] || ""} disabled={disabled} onChange={(e) => set({ header: { ...h, [key]: e.target.value } })} {...props} />
    </Form.Item>
  );
  const date = (key, label) => (
    <Form.Item label={label} style={{ marginBottom: 10 }}>
      <DatePicker
        style={{ width: "100%" }} disabled={disabled} format="MMM D, YYYY"
        value={h[key] ? moment(h[key], "YYYY-MM-DD") : null}
        onChange={(d) => set({ header: { ...h, [key]: d ? d.format("YYYY-MM-DD") : "" } })}
      />
    </Form.Item>
  );
  return (
    <Form layout="vertical">
      {field("subject", "Subject", { placeholder: "e.g. TC-303A Keynote 2 - Sleeve relocation", maxLength: 300 })}
      <Row gutter={12}>
        <Col xs={24} md={12}>{field("to", "To", { placeholder: "Your company template's default if left empty" })}</Col>
        <Col xs={24} md={12}>{field("from", "From", { placeholder: "Your company template's default if left empty" })}</Col>
      </Row>
      <Row gutter={12}>
        <Col xs={12} md={6}>{date("dateInitiated", "Date initiated")}</Col>
        <Col xs={12} md={6}>{date("dueDate", "Due date")}</Col>
        <Col xs={24} md={12}>{field("location", "Location", { placeholder: "e.g. Level 3 - Grid C-D / 4-5" })}</Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} md={12}>{field("reference", "Reference", { placeholder: "e.g. Oct 19 coordination meeting" })}</Col>
        <Col xs={24} md={12}>
          <Form.Item label="Drawing numbers" style={{ marginBottom: 10 }} tooltip="Empty: the sheet numbers below are used">
            <Select
              mode="tags" disabled={disabled} value={h.drawingNumbers || []} tokenSeparators={[",", " "]}
              onChange={(v) => set({ header: { ...h, drawingNumbers: v } })} placeholder="e.g. E-303B"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={12} md={6}>{field("costImpact", "Cost impact", { placeholder: "TBD" })}</Col>
        <Col xs={12} md={6}>{field("scheduleImpact", "Schedule impact", { placeholder: "TBD" })}</Col>
        <Col xs={12} md={6}>{field("specSection", "Spec section")}</Col>
        <Col xs={12} md={6}>{field("costCode", "Cost code")}</Col>
      </Row>
      <Row gutter={12}>
        <Col xs={12} md={6}>
          <Form.Item label="Revision" style={{ marginBottom: 10 }}>
            <InputNumber min={0} max={99} disabled={disabled} value={h.revision || 0} onChange={(v) => set({ header: { ...h, revision: v || 0 } })} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>{field("rfiNumber", "RFI number", { placeholder: "Prints as RFI #__ until set" })}</Col>
      </Row>
    </Form>
  );
}

function QuestionFields({ c, set, disabled }) {
  return (
    <Form layout="vertical">
      <Form.Item label="Question" extra={FIELD_HELP} style={{ marginBottom: 10 }}>
        <Input.TextArea
          rows={7} disabled={disabled} value={c.question}
          placeholder={"Existing condition, why it does not work, what you propose.\n\nLeave a blank line between paragraphs."}
          onChange={(e) => set({ question: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Numbered asks" style={{ marginBottom: 0 }}>
        {c.asks.map((a, k) => (
          // eslint-disable-next-line react/no-array-index-key
          <Space key={k} style={{ display: "flex", marginBottom: 6 }} align="start">
            <Text type="secondary" style={{ width: 18, display: "inline-block" }}>{k + 1}.</Text>
            <Input
              style={{ width: 560, maxWidth: "calc(70 * var(--vw))" }} disabled={disabled} value={a}
              onChange={(e) => set({ asks: c.asks.map((x, j) => (j === k ? e.target.value : x)) })}
            />
            {!disabled && <Button type="text" icon={<DeleteOutlined />} onClick={() => set({ asks: c.asks.filter((_, j) => j !== k) })} />}
          </Space>
        ))}
        {!disabled && c.asks.length < 12 && (
          <Button size="small" icon={<PlusOutlined />} onClick={() => set({ asks: [...c.asks, ""] })}>Add an ask</Button>
        )}
      </Form.Item>
    </Form>
  );
}

function SheetFields({ projectId, c, set, disabled }) {
  const [options, setOptions] = useState([]);
  const [marking, setMarking] = useState(-1);
  const [busy, setBusy] = useState(false);
  const search = useCallback((q) => {
    setBusy(true);
    listRfiSheets(projectId, q)
      .then((r) => setOptions(r.sheets || []))
      .catch(() => setOptions([]))
      .finally(() => setBusy(false));
  }, [projectId]);
  useEffect(() => { if (!disabled) search(""); }, [search, disabled]);
  const add = (docId) => {
    const s = options.find((o) => o.docId === docId);
    if (!s || c.sheets.some((x) => x.docId === docId) || c.sheets.length >= 10) return;
    set({ sheets: [...c.sheets, { docId, name: s.name, sheetNo: s.sheetNo, page: 1, stripMarkups: true }] });
  };
  const patch = (k, p) => set({ sheets: c.sheets.map((s, j) => (j === k ? { ...s, ...p } : s)) });
  const cloudsOn = (docId) => c.locations.filter((l) => l.sheetDocId === docId).reduce((n, l) => n + (l.clouds || []).length, 0);
  // Taking a sheet off takes the clouds drawn on it too: they have nowhere
  // else to be drawn.
  const removeSheet = (k) => {
    const gone = c.sheets[k].docId;
    set({
      sheets: c.sheets.filter((_, j) => j !== k),
      locations: c.locations.map((l) => (l.sheetDocId === gone ? { ...l, sheetDocId: "", clouds: [] } : l)),
    });
  };
  return (
    <div>
      {!disabled && (
        <Select
          showSearch value={null} placeholder="Add a sheet from Document Control" style={{ width: "100%", marginBottom: 10 }}
          filterOption={false} onSearch={search} onChange={add} loading={busy}
          notFoundContent={busy ? <Spin size="small" /> : "No PDF in Document Control matches"}
          options={options.map((o) => ({ value: o.docId, label: [o.sheetNo, o.name].filter(Boolean).join(" - ") || o.docId }))}
        />
      )}
      {!c.sheets.length && <Text type="secondary">No sheet yet. Each sheet becomes its own exhibit page, kept as vector.</Text>}
      {c.sheets.map((s, k) => (
        <div key={s.docId} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap" }}>
          <Tag>Exhibit sheet</Tag>
          <span style={{ flex: "1 1 220px", minWidth: 0 }}>{[s.sheetNo, s.name].filter(Boolean).join(" - ")}</span>
          <span>Page <InputNumber size="small" min={1} max={999} disabled={disabled} value={s.page} onChange={(v) => patch(k, { page: v || 1 })} style={{ width: 64 }} /></span>
          <Input size="small" placeholder="Sheet no." disabled={disabled} value={s.sheetNo} onChange={(e) => patch(k, { sheetNo: e.target.value })} style={{ width: 110 }} />
          <Tooltip title="Old markups on the sheet are removed from the exhibit; links are kept">
            <span><Switch size="small" disabled={disabled} checked={s.stripMarkups !== false} onChange={(v) => patch(k, { stripMarkups: v })} /> Remove old markups</span>
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => setMarking(k)}>
            {disabled ? "View marks" : "Mark up"}{cloudsOn(s.docId) ? ` (${cloudsOn(s.docId)})` : ""}
          </Button>
          {s.stampAt ? <Tag color="red">Stamp placed</Tag> : <Tooltip title="Place it from Mark up"><Tag>Stamp top-left</Tag></Tooltip>}
          <Tooltip title="An extra page after this sheet: an enlarged view of the area you draw in Mark up (or around the clouds), each location ringed and numbered">
            <span><Switch size="small" disabled={disabled} checked={!!s.keyPlan} onChange={(v) => patch(k, { keyPlan: v })} /> Key plan page</span>
          </Tooltip>
          {s.keyPlan && (
            <>
              <Tooltip title="A table of the locations beside the key plan">
                <span><Switch size="small" disabled={disabled} checked={s.keyPlanTable !== false} onChange={(v) => patch(k, { keyPlanTable: v })} /> Location table</span>
              </Tooltip>
              {s.keyPlanRect ? <Tag color="blue">Area drawn</Tag> : <Tooltip title="Draw the area from Mark up"><Tag>Around the clouds</Tag></Tooltip>}
            </>
          )}
          {!disabled && (cloudsOn(s.docId)
            ? (
              <Popconfirm title={`Remove this sheet and its ${cloudsOn(s.docId)} cloud${cloudsOn(s.docId) === 1 ? "" : "s"}?`} onConfirm={() => removeSheet(k)}>
                <Button type="text" icon={<DeleteOutlined />} />
              </Popconfirm>
            )
            : <Button type="text" icon={<DeleteOutlined />} onClick={() => removeSheet(k)} />)}
        </div>
      ))}
      {marking >= 0 && c.sheets[marking] && (
        <SheetMarkup
          projectId={projectId} sheet={c.sheets[marking]} sheetIndex={marking} c={c} set={set} disabled={disabled}
          onClose={() => setMarking(-1)}
        />
      )}
    </div>
  );
}

function ImageFields({ projectId, rfi, c, set, disabled, onRecord }) {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    try {
      const r = await uploadRfiImages(projectId, rfi._id, files);
      onRecord(r.rfi, { keepEdits: true });
    } catch (e) {
      message.error(e.message || "Could not add the pictures");
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try {
      const r = await deleteRfiImage(projectId, rfi._id, id);
      onRecord(r.rfi, { keepEdits: true });
    } catch (e) { message.error(e.message || "Could not remove the picture"); }
  };
  return (
    <div>
      {!disabled && (
        <Upload
          multiple accept="image/png,image/jpeg,image/webp" showUploadList={false}
          beforeUpload={(file, list) => { if (file === list[list.length - 1]) upload(list); return false; }}
        >
          <Button icon={<UploadOutlined />} loading={busy}>Add pictures</Button>
        </Upload>
      )}
      <Text type="secondary" style={{ display: "block", margin: "6px 0 10px" }}>
        Model views, enlarged plans, site photos. A picture used on a location row shows there; the rest go on model-view pages, two per page.
      </Text>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {c.images.map((img) => (
          <div key={img.id} style={{ width: 180 }}>
            <AuthImage projectId={projectId} rfiId={rfi._id} imageId={img.id} style={{ width: 180, height: 120, objectFit: "cover", borderRadius: 4, border: "1px solid #eee" }} />
            <Input
              size="small" placeholder="Caption" disabled={disabled} value={img.caption || ""} style={{ marginTop: 4 }}
              onChange={(e) => set({ images: c.images.map((x) => (x.id === img.id ? { ...x, caption: e.target.value } : x)) })}
            />
            {!disabled && (
              <Popconfirm title="Remove this picture?" onConfirm={() => remove(img.id)}>
                <Button size="small" type="link" danger style={{ padding: 0 }}>Remove</Button>
              </Popconfirm>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationFields({ c, set, disabled }) {
  const patch = (k, p) => set({ locations: c.locations.map((l, j) => (j === k ? { ...l, ...p } : l)) });
  const sheetName = (docId) => {
    const s = c.sheets.find((x) => x.docId === docId);
    return s ? ([s.sheetNo, s.name].filter(Boolean).join(" - ") || "a sheet") : "";
  };
  const imageOptions = c.images.map((i, k) => ({ value: i.id, label: i.caption || i.name || `Picture ${k + 1}` }));
  return (
    <div>
      {!c.locations.length && <Text type="secondary">One location is enough for a single issue. Several locations get numbered rows, three per page.</Text>}
      {c.locations.map((l, k) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={k} style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <Space style={{ marginBottom: 6 }}>
            <Tag color="red">{k + 1}</Tag>
            <Input size="small" placeholder="Grid" disabled={disabled} value={l.grid} onChange={(e) => patch(k, { grid: e.target.value })} style={{ width: 160 }} />
            <Input size="small" placeholder="Room" disabled={disabled} value={l.room} onChange={(e) => patch(k, { room: e.target.value })} style={{ width: 140 }} />
            {(l.clouds || []).length && sheetName(l.sheetDocId)
              ? <Text type="secondary">{l.clouds.length} cloud{l.clouds.length === 1 ? "" : "s"} on {sheetName(l.sheetDocId)}</Text>
              : (l.clouds || []).length
                ? <Text type="warning">Clouds on a sheet no longer on this RFI</Text>
                : <Text type="secondary">Not marked on a sheet - use Mark up on a sheet</Text>}
            {!disabled && (l.clouds || []).length > 0 && (
              <Button size="small" type="link" onClick={() => patch(k, { clouds: [], sheetDocId: "" })}>Clear marks</Button>
            )}
            {!disabled && <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => set({ locations: c.locations.filter((_, j) => j !== k) })} />}
          </Space>
          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Input.TextArea rows={2} placeholder="Clash: what is wrong here" disabled={disabled} value={l.clash} onChange={(e) => patch(k, { clash: e.target.value })} />
            </Col>
            <Col xs={24} md={12}>
              <Input.TextArea rows={2} placeholder="Proposed: what you want to do" disabled={disabled} value={l.proposed} onChange={(e) => patch(k, { proposed: e.target.value })} />
            </Col>
          </Row>
          <Select
            mode="multiple" size="small" placeholder="Pictures on this row (up to 3)" disabled={disabled || !imageOptions.length}
            value={l.imageIds || []} options={imageOptions} style={{ width: "100%", marginTop: 6 }}
            onChange={(v) => patch(k, { imageIds: v.slice(0, 3) })}
          />
        </div>
      ))}
      {!disabled && c.locations.length < 30 && (
        <Button size="small" icon={<PlusOutlined />} onClick={() => set({ locations: [...c.locations, { grid: "", room: "", clash: "", proposed: "", imageIds: [], sheetDocId: "", clouds: [] }] })}>
          Add a location
        </Button>
      )}
    </div>
  );
}

function BuildStatus({ rfi }) {
  const last = rfi.latestBuild;
  if (!last) return <Alert type="info" showIcon message="Not built yet. Save, then Build package to file the PDF in Document Control." />;
  const warnings = last.warnings || [];
  const block = warnings.filter((w) => w.severity === "block");
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {!rfi.buildIsCurrent && (
        <Alert type="warning" showIcon message="The package is older than the latest changes - build it again before approving." />
      )}
      {block.map((w, k) => (
        // eslint-disable-next-line react/no-array-index-key
        <Alert key={`b${k}`} type="error" showIcon message={w.message} />
      ))}
      {warnings.filter((w) => w.severity !== "block").map((w, k) => (
        // eslint-disable-next-line react/no-array-index-key
        <Alert key={`w${k}`} type="warning" showIcon message={w.message} />
      ))}
      <Text type="secondary">
        Last build: revision {last.rev}, {last.pageCount} page{last.pageCount === 1 ? "" : "s"}, {moment(last.at).format("MMM D, h:mm a")}
        {last.by && last.by.name ? ` by ${last.by.name}` : ""}. Filed in Document Control as "{last.fileName}".
      </Text>
    </Space>
  );
}

export default function RfiEditor({ projectId, rfi, onClose, onChanged }) {
  const [rec, setRec] = useState(rfi);
  const [c, setC] = useState(() => copyOf(rfi));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [numberAsk, setNumberAsk] = useState(false);
  const [number, setNumber] = useState("");

  const can = rec.can || {};
  const disabled = !can.edit;
  const set = (p) => { setC((x) => ({ ...x, ...p })); setDirty(true); };

  // A fresh record from the server. Picture uploads keep the page's unsaved
  // edits and take only the new picture list and revision.
  const onRecord = useCallback((next, { keepEdits } = {}) => {
    setRec(next);
    if (keepEdits) {
      setC((x) => {
        const fresh = copyOf(next);
        const caption = new Map(x.images.map((i) => [i.id, i.caption]));
        return { ...x, rev: fresh.rev, images: fresh.images.map((i) => ({ ...i, caption: caption.has(i.id) ? caption.get(i.id) : i.caption })) };
      });
    } else {
      setC(copyOf(next));
      setDirty(false);
    }
    onChanged();
  }, [onChanged]);

  const run = async (label, fn, ok) => {
    setBusy(label);
    try {
      const r = await fn();
      if (r && r.rfi) onRecord(r.rfi);
      if (ok) message.success(typeof ok === "function" ? ok(r) : ok);
      return r;
    } catch (e) {
      message.error(e.message || "That did not work");
      return null;
    } finally { setBusy(""); }
  };

  const save = () => run("save", () => saveRfi(projectId, rec._id, bodyOf(c)), "Saved");
  const build = async () => {
    if (dirty && can.edit) {
      const saved = await run("save", () => saveRfi(projectId, rec._id, bodyOf(c)));
      if (!saved) return;
    }
    await run("build", () => buildRfi(projectId, rec._id), (r) => (r.warnings && r.warnings.length
      ? `Built with ${r.warnings.length} thing${r.warnings.length === 1 ? "" : "s"} to look at`
      : "Built and filed in Document Control"));
  };
  const openPdf = async () => {
    setBusy("pdf");
    try {
      const blob = await fetchRfiPdf(projectId, rec._id);
      const href = URL.createObjectURL(blob);
      window.open(href, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      message.error(e.message || "Could not open the package");
    } finally { setBusy(""); }
  };

  const status = STATUS[rec.status] || { label: rec.status, color: "default" };
  const title = useMemo(() => (
    <Space wrap>
      <span>RFI draft {rec.seq}</span>
      <Tag color={status.color}>{status.label}</Tag>
      {dirty && <Tag color="orange">Unsaved changes</Tag>}
    </Space>
  ), [rec.seq, status, dirty]);

  return (
    <Drawer
      open width={Math.min(1040, window.innerWidth)} title={title} destroyOnClose
      onClose={() => {
        if (!dirty) return onClose();
        return Modal.confirm({ title: "Leave without saving?", okText: "Leave", onOk: onClose });
      }}
      extra={(
        <Space wrap>
          {can.edit && <Button icon={<SaveOutlined />} onClick={save} loading={busy === "save"} disabled={!dirty}>Save</Button>}
          {can.build && <Button type="primary" icon={<ToolOutlined />} onClick={build} loading={busy === "build"}>Build package</Button>}
          {rec.latestBuild && <Button icon={<FilePdfOutlined />} onClick={openPdf} loading={busy === "pdf"}>Open PDF</Button>}
        </Space>
      )}
    >
      <BuildStatus rfi={rec} />
      <Space wrap style={{ margin: "12px 0" }}>
        {can.sendForReview && (
          <Button icon={<SendOutlined />} disabled={dirty} loading={busy === "send"}
                  onClick={() => run("send", () => sendRfiForReview(projectId, rec._id), "Sent for review - the project team can now approve it")}>
            Send for review
          </Button>
        )}
        {can.approve && (
          <Tooltip title={!rec.buildIsCurrent ? "Build the package from the latest changes first" : ""}>
            <Button icon={<CheckOutlined />} disabled={dirty || !rec.buildIsCurrent} loading={busy === "approve"}
                    onClick={() => run("approve", () => approveRfi(projectId, rec._id), "Approved")}>
              Approve
            </Button>
          </Tooltip>
        )}
        {can.markSubmitted && <Button onClick={() => { setNumber(rec.header.rfiNumber || ""); setNumberAsk(true); }}>Mark submitted</Button>}
        {can.reopen && (
          <Button icon={<RollbackOutlined />} loading={busy === "reopen"} onClick={() => run("reopen", () => reopenRfi(projectId, rec._id), "Reopened for changes")}>
            Reopen
          </Button>
        )}
        {can.remove && (
          <Popconfirm title="Delete this draft?" onConfirm={() => run("delete", () => deleteRfi(projectId, rec._id), "Deleted").then((r) => { if (r) { onChanged(); onClose(); } })}>
            <Button danger icon={<DeleteOutlined />}>Delete draft</Button>
          </Popconfirm>
        )}
        {rec.approvedBy && rec.approvedBy.name && <Text type="secondary">Approved by {rec.approvedBy.name} {moment(rec.approvedAt).format("MMM D")}</Text>}
      </Space>
      {disabled && rec.status !== "draft" && rec.status !== "review" && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Approved RFIs are locked so the package matches what was approved. Reopen it to make changes." />
      )}

      <Divider orientation="left">Header</Divider>
      <ExtraScopeChip extraScope={rec.extraScope} />
      <ScopeFields projectId={projectId} c={c} set={set} disabled={disabled} />
      <HeaderFields c={c} set={set} disabled={disabled} />
      <Divider orientation="left">Question</Divider>
      <QuestionFields c={c} set={set} disabled={disabled} />
      <Divider orientation="left">Sheets</Divider>
      <SheetFields projectId={projectId} c={c} set={set} disabled={disabled} />
      <Divider orientation="left">Pictures</Divider>
      <ImageFields projectId={projectId} rfi={rec} c={c} set={set} disabled={disabled} onRecord={onRecord} />
      <Divider orientation="left">Locations</Divider>
      <LocationFields c={c} set={set} disabled={disabled} />
      {!c.sheets.length && !c.images.length && !c.locations.length && (
        <Empty style={{ marginTop: 16 }} description="Add a sheet, a picture or a location: the package is the cover plus these." />
      )}

      <Modal
        open={numberAsk} title="Mark submitted" okText="Mark submitted" onCancel={() => setNumberAsk(false)}
        confirmLoading={busy === "submitted"}
        onOk={async () => {
          const r = await run("submitted", () => markRfiSubmitted(projectId, rec._id, { rfiNumber: number }), "Marked submitted");
          if (r) setNumberAsk(false);
        }}
      >
        <p>Type the number the RFI was given when it was entered.</p>
        <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="e.g. 14" autoFocus />
      </Modal>
    </Drawer>
  );
}
